import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "sonner";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ChatWidget from "@/components/ChatWidget";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import AdminLayout from "@/components/AdminLayout";

import HomePage from "@/pages/HomePage";
import ProductsPage from "@/pages/ProductsPage";
import ServicesPage from "@/pages/ServicesPage";
import OrderPage from "@/pages/OrderPage";
import AIDesignerPage from "@/pages/AIDesignerPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AuthCallback from "@/pages/AuthCallback";
import CustomerDashboard from "@/pages/CustomerDashboard";
import MyOrdersPage from "@/pages/MyOrdersPage";
import CheckoutPage from "@/pages/CheckoutPage";
import TrackPage from "@/pages/TrackPage";

import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminOrdersPage from "@/pages/admin/AdminOrdersPage";
import AdminNewOrderPage from "@/pages/admin/AdminNewOrderPage";
import UsersPage from "@/pages/admin/UsersPage";
import CustomersPage from "@/pages/admin/CustomersPage";
import SetPasswordPage from "@/pages/SetPasswordPage";
import ProfilePage from "@/pages/ProfilePage";
import CustomerDetailPage from "@/pages/admin/CustomerDetailPage";
import ReportsPage from "@/pages/admin/ReportsPage";
import PrintServicesPage from "@/pages/admin/PrintServicesPage";
import ExpensesPage from "@/pages/admin/ExpensesPage";
import SuppliesPage from "@/pages/admin/SuppliesPage";
import SuppliersPage from "@/pages/admin/SuppliersPage";
import SettingsPage from "@/pages/admin/SettingsPage";
import AdminPricing from "@/pages/admin/AdminPricing";
import CashierDashboard from "@/pages/admin/CashierDashboard";
import ProductionDashboard from "@/pages/admin/ProductionDashboard";
import ReleasingDashboard from "@/pages/admin/ReleasingDashboard";
import AdminChatInbox from "@/pages/admin/AdminChatInbox";
import AdminLayoutRequestsPage from "@/pages/admin/AdminLayoutRequestsPage";
import AdminAIDesigner from "@/pages/admin/AdminAIDesigner";
import AdminAILogs from "@/pages/admin/AdminAILogs";

import "@/App.css";

const PublicLayout = ({ children }) => (
  <>
    <Navbar />
    <main className="min-h-[60vh] pt-24">{children}</main>
    <Footer />
    <ChatWidget />
  </>
);

const AppShell = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Admin routes (no public chrome) */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route element={<ProtectedRoute role="admin"><AdminLayout /></ProtectedRoute>}>
        <Route path="/admin" element={<ProtectedRoute role="admin" perm="analytics.view"><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/orders" element={<ProtectedRoute role="admin" perm="orders.view"><AdminOrdersPage /></ProtectedRoute>} />
        <Route path="/admin/orders/new" element={<ProtectedRoute role="admin" perm="orders.create"><AdminNewOrderPage /></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute role="admin" perm="users.manage"><UsersPage /></ProtectedRoute>} />
        <Route path="/admin/customers" element={<ProtectedRoute role="admin" perm="customers.view"><CustomersPage /></ProtectedRoute>} />
        <Route path="/admin/customers/:id" element={<ProtectedRoute role="admin" perm="customers.view"><CustomerDetailPage /></ProtectedRoute>} />
        <Route path="/admin/reports" element={<ProtectedRoute role="admin" perm="analytics.view"><ReportsPage /></ProtectedRoute>} />
        <Route path="/admin/printservices" element={<ProtectedRoute role="admin" perm="services.manage"><PrintServicesPage /></ProtectedRoute>} />
        <Route path="/admin/expenses" element={<ProtectedRoute role="admin" perm="expenses.manage"><ExpensesPage /></ProtectedRoute>} />
        <Route path="/admin/supplies" element={<ProtectedRoute role="admin" perm="supplies.manage"><SuppliesPage /></ProtectedRoute>} />
        <Route path="/admin/suppliers" element={<ProtectedRoute role="admin" perm="supplies.manage"><SuppliersPage /></ProtectedRoute>} />
        <Route path="/admin/pricing" element={<ProtectedRoute role="admin" perm="pricing.manage"><AdminPricing /></ProtectedRoute>} />
        <Route path="/admin/settings" element={<ProtectedRoute role="admin" perm="pricing.manage"><SettingsPage /></ProtectedRoute>} />
        <Route path="/admin/chat-inbox" element={<ProtectedRoute role="admin" perm="orders.view"><AdminChatInbox /></ProtectedRoute>} />
        <Route path="/admin/layout-requests" element={<ProtectedRoute role="admin" perm="orders.view"><AdminLayoutRequestsPage /></ProtectedRoute>} />
        <Route path="/admin/ai-designer" element={<ProtectedRoute role="admin" perm="orders.view"><AdminAIDesigner /></ProtectedRoute>} />
        <Route path="/admin/ai-logs" element={<ProtectedRoute role="admin" perm="analytics.view"><AdminAILogs /></ProtectedRoute>} />
        <Route path="/cashier" element={<ProtectedRoute role="admin" perm="payments.confirm"><CashierDashboard /></ProtectedRoute>} />
        <Route path="/production" element={<ProtectedRoute role="admin" perm="production.update"><ProductionDashboard /></ProtectedRoute>} />
        <Route path="/releasing" element={<ProtectedRoute role="admin" perm="releasing.update"><ReleasingDashboard /></ProtectedRoute>} />
      </Route>

      {/* Public + customer routes */}
      <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
      <Route path="/products" element={<PublicLayout><ProductsPage /></PublicLayout>} />
      <Route path="/services" element={<PublicLayout><ServicesPage /></PublicLayout>} />
      <Route path="/order" element={<PublicLayout><OrderPage /></PublicLayout>} />
      <Route path="/ai-designer" element={<PublicLayout><ProtectedRoute><AIDesignerPage /></ProtectedRoute></PublicLayout>} />
      <Route path="/login" element={<PublicLayout><LoginPage /></PublicLayout>} />
      <Route path="/set-password" element={<PublicLayout><SetPasswordPage /></PublicLayout>} />
      <Route path="/register" element={<PublicLayout><RegisterPage /></PublicLayout>} />
      <Route path="/checkout/:orderId" element={<PublicLayout><CheckoutPage /></PublicLayout>} />
      <Route path="/track" element={<PublicLayout><TrackPage /></PublicLayout>} />
      <Route path="/dashboard" element={<PublicLayout><ProtectedRoute><CustomerDashboard /></ProtectedRoute></PublicLayout>} />
      <Route path="/dashboard/orders" element={<PublicLayout><ProtectedRoute><MyOrdersPage /></ProtectedRoute></PublicLayout>} />
      <Route path="/dashboard/profile" element={<PublicLayout><ProtectedRoute><ProfilePage /></ProtectedRoute></PublicLayout>} />

      <Route path="*" element={<PublicLayout><div className="p-20 text-center text-neutral-500">404 — Page not found</div></PublicLayout>} />
    </Routes>
  );
};

const App = () => (
  <BrowserRouter>
    <GoogleReCaptchaProvider
      reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
      scriptProps={{ async: true, defer: true }}
    >
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <AppShell />
      </AuthProvider>
    </GoogleReCaptchaProvider>
  </BrowserRouter>
);

export default App;
